import { z } from 'zod'

// Shared per-entity attributes input contract (TO-418, ADR-0042). Keys are
// namespaced and lowercase (`abanca.segment`); `null` is never a value — a key
// is removed by deleting it.

/** Namespaced lowercase key, ≤ 128 characters. */
export const entityAttributeKeySchema = z
	.string()
	.max(128)
	.regex(/^[a-z][a-z0-9_]*(\.[a-z0-9_]+)+$/, 'key must be namespaced lowercase, e.g. abanca.segment')

/** string (≤ 1000) | finite number | boolean | string[] (≤ 50 items of ≤ 200). */
export const entityAttributeValueSchema = z.union([
	z.string().max(1000),
	z.number().refine(Number.isFinite, { message: 'number must be finite' }),
	z.boolean(),
	z.array(z.string().max(200)).max(50),
])

const MAX_KEYS_PER_CALL = 100

export const entityAttributeUpsertSchema = z
	.object({
		attributes: z
			.record(entityAttributeKeySchema, entityAttributeValueSchema)
			.refine((value) => Object.keys(value).length >= 1, { message: 'at least one attribute is required' })
			.refine((value) => Object.keys(value).length <= MAX_KEYS_PER_CALL, {
				message: `at most ${MAX_KEYS_PER_CALL} attributes per call`,
			}),
		source_ref: z.string().min(1).max(255).optional(),
	})
	.strict()

/** 1–100 keys (the delete surface). */
export const entityAttributeKeysSchema = z.array(entityAttributeKeySchema).min(1).max(MAX_KEYS_PER_CALL)

export type EntityAttributeUpsertInput = z.infer<typeof entityAttributeUpsertSchema>
